import React, { useState } from 'react';
import { 
  FaAtom, 
  FaDna, 
  FaMicroscope, 
  FaFlask, 
  FaMicrochip, 
  FaLeaf, 
  FaGlobe, 
  FaBolt, 
  FaBrain,
  FaShieldAlt,
  FaRocket,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaBook,
  FaUsers,
  FaTrophy,
  FaCogs,
  FaLightbulb,
  FaSearch,
  FaChartBar,
  FaArrowsAlt
} from 'react-icons/fa';

const AIScientificResearch = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const researchFields = [
    {
      icon: <FaDna size={40} />,
      title: "Biotechnology & Genomics",
      description: "AI-driven protein folding, CRISPR optimization, and personalized medicine development",
      applications: ["Gene therapy design", "Protein structure prediction", "Biomarker discovery", "Drug target identification"],
      impact: "Accelerating drug discovery by 10x and enabling precision medicine for complex diseases",
      color: "success"
    },
    {
      icon: <FaFlask size={40} />,
      title: "Chemistry & Materials Science",
      description: "Molecular design, reaction prediction, and novel material discovery through AI algorithms",
      applications: ["Catalyst design", "Polymer optimization", "Chemical synthesis planning", "Material property prediction"],
      impact: "Reducing material development time from decades to months with 90% accuracy in property prediction",
      color: "primary"
    },
    {
      icon: <FaAtom size={40} />,
      title: "Physics & Quantum Computing",
      description: "Quantum algorithm optimization, particle physics simulation, and theoretical breakthrough acceleration",
      applications: ["Quantum error correction", "Many-body problem solving", "High-energy physics modeling", "Condensed matter research"],
      impact: "Enabling quantum supremacy applications and solving previously intractable physics problems",
      color: "info"
    },
    {
      icon: <FaMicroscope size={40} />,
      title: "Nanotechnology",
      description: "AI-guided nanoparticle design, self-assembly prediction, and nanoscale manufacturing optimization",
      applications: ["Nanoparticle synthesis", "Surface modification", "Quantum dots engineering", "Nanocomposite design"],
      impact: "Creating next-generation electronics, medical devices, and energy storage solutions",
      color: "warning"
    },
    {
      icon: <FaGlobe size={40} />,
      title: "Climate & Environmental Science",
      description: "Advanced climate modeling, carbon capture optimization, and ecosystem preservation strategies",
      applications: ["Weather prediction", "Carbon sequestration", "Biodiversity monitoring", "Pollution control"],
      impact: "Providing accurate climate predictions and developing sustainable technologies for carbon neutrality",
      color: "secondary"
    },
    {
      icon: <FaLeaf size={40} />,
      title: "Agricultural Innovation",
      description: "Precision farming, crop optimization, and sustainable food production through AI analytics",
      applications: ["Crop yield prediction", "Pest management", "Soil optimization", "Vertical farming"],
      impact: "Increasing crop yields by 40% while reducing resource consumption and environmental impact",
      color: "success"
    },
    {
      icon: <FaBrain size={40} />,
      title: "Neuroscience & AI",
      description: "Brain-computer interfaces, neurological disease research, and cognitive enhancement technologies",
      applications: ["Neural pathway mapping", "Brain-AI interfaces", "Neurological drug discovery", "Cognitive modeling"],
      impact: "Revolutionizing treatment of neurological disorders and advancing human-AI collaboration",
      color: "danger"
    },
    {
      icon: <FaRocket size={40} />,
      title: "Space & Aerospace",
      description: "Mission planning optimization, materials for extreme environments, and extraterrestrial research",
      applications: ["Spacecraft design", "Mission trajectory optimization", "Space materials", "Astrobiology research"],
      impact: "Enabling sustainable space exploration and advancing our understanding of the universe",
      color: "dark"
    }
  ];

  const futureTrends = [
    {
      year: "2026",
      title: "AI-Driven Drug Discovery Revolution",
      description: "Fully automated drug discovery pipelines reducing development time from 10-15 years to 2-3 years",
      fields: ["Pharmaceutical Research", "Computational Biology", "Chemical Informatics"],
      color: "primary"
    },
    {
      year: "2026",
      title: "Quantum-Classical Hybrid Computing",
      description: "Integration of quantum and classical AI for solving complex optimization problems in materials science",
      fields: ["Quantum Computing", "Materials Science", "Optimization Theory"],
      color: "info"
    },
    {
      year: "2027",
      title: "Autonomous Laboratory Systems",
      description: "Self-operating labs conducting experiments 24/7 with AI planning, execution, and analysis",
      fields: ["Laboratory Automation", "Robotics", "Experimental Design"],
      color: "success"
    },
    {
      year: "2027",
      title: "Climate Engineering Solutions",
      description: "AI-designed atmospheric processors and carbon capture systems for large-scale climate intervention",
      fields: ["Climate Science", "Environmental Engineering", "Atmospheric Chemistry"],
      color: "warning"
    }
  ];

  const capabilities = [
    {
      icon: <FaArrowsAlt size={30} />,
      title: "Precision Research",
      description: "AI algorithms identify optimal research directions with 95% accuracy, reducing failed experiments",
      color: "primary"
    },
    {
      icon: <FaChartLine size={30} />,
      title: "Accelerated Discovery",
      description: "Machine learning models predict outcomes 1000x faster than traditional computational methods",
      color: "success"
    },
    {
      icon: <FaClock size={30} />,
      title: "24/7 Analysis",
      description: "Continuous data processing and hypothesis generation, never missing critical insights",
      color: "info"
    },
    {
      icon: <FaShieldAlt size={30} />,
      title: "Risk Mitigation",
      description: "Predictive models identify potential safety issues and environmental impacts before implementation",
      color: "warning"
    }
  ];

  const aiFeatures = [
    {
      icon: <FaBrain size={24} />,
      title: "Intelligent Hypothesis Generation",
      description: "AI analyzes vast datasets to generate novel hypotheses that humans might never consider, leading to breakthrough discoveries."
    },
    {
      icon: <FaBolt size={24} />,
      title: "Automated Experimentation",
      description: "Robotic systems guided by AI conduct thousands of experiments simultaneously, optimizing parameters in real-time."
    },
    {
      icon: <FaArrowRight size={24} />,
      title: "Predictive Modeling",
      description: "Machine learning models predict experimental outcomes with high accuracy, eliminating costly failed trials."
    }
  ];

  const workflowSteps = [
    { step: 1, title: "Data Ingestion", description: "Multi-source scientific data integration and preprocessing", color: "primary" },
    { step: 2, title: "Pattern Analysis", description: "AI identifies hidden patterns and correlations in complex datasets", color: "success" },
    { step: 3, title: "Hypothesis Generation", description: "Automated generation of testable scientific hypotheses", color: "info" },
    { step: 4, title: "Experiment Design", description: "Optimal experimental parameter selection and protocol generation", color: "warning" },
    { step: 5, title: "Results Analysis", description: "Real-time interpretation and next-step recommendation", color: "danger" }
  ];

  return (
    <div>

     
      
      {/* Hero Section */}
      <div className="bg-dark text-white position-relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        minHeight: '100vh'
      }}>
        <div className="container py-5">
          <div className="row justify-content-center text-center py-5">
            <div className="col-lg-10">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center bg-gradient p-4 rounded-circle" 
                     style={{ background: 'linear-gradient(45deg, #20c997, #17a2b8)' }}>
                  <FaFlask size={60} className="text-white" />
                </div>
              </div>
              <h1 className="display-2 fw-bold mb-4 text-gradient">
                AI-Powered Scientific Research
              </h1>
              <p className="lead fs-3 mb-4 text-light opacity-75">
                Revolutionizing scientific discovery through AI applications in biology, 
                chemistry, physics, drug discovery, climate modeling, and agricultural innovation 
                for breakthrough research.
              </p>
              <button className="btn btn-lg px-5 py-3 fw-bold text-uppercase" 
                      style={{ 
                        background: 'linear-gradient(45deg, #20c997, #17a2b8)',
                        border: 'none',
                        borderRadius: '50px',
                        color: 'white'
                      }}>
                <FaSearch className="me-2" />
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="container py-5">
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10">
            <div className="nav nav-pills justify-content-center gap-3" role="tablist">
              {[
                { id: 'overview', label: 'Overview', icon: <FaBook className="me-2" /> },
                { id: 'fields', label: 'Research Fields', icon: <FaMicroscope className="me-2" /> },
                { id: 'future', label: 'Future Trends', icon: <FaRocket className="me-2" /> },
                { id: 'capabilities', label: 'AI Capabilities', icon: <FaCogs className="me-2" /> }
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`btn btn-lg px-4 py-3 fw-semibold ${activeTab === tab.id ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={{ borderRadius: '50px' }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-pane fade show active">
              {/* Problem Statement */}
              <div className="row mb-5">
                <div className="col-lg-6 mb-4">
                  <div className="card h-100 border-0 shadow-lg">
                    <div className="card-body p-5">
                      <h2 className="card-title display-5 fw-bold text-primary mb-4">
                        The Scientific Research Challenge
                      </h2>
                      <p className="card-text fs-5 text-muted mb-4">
                        Traditional scientific research faces unprecedented challenges: exponentially growing data volumes, 
                        complex multi-variable problems, and the need for faster discovery cycles to address global challenges 
                        like climate change, disease, and resource scarcity.
                      </p>
                      <div className="alert alert-danger border-start border-5 border-danger">
                        <h5 className="alert-heading fw-bold">
                          <FaChartBar className="me-2" />
                          Current Limitations:
                        </h5>
                        <ul className="mb-0 list-unstyled">
                          <li className="mb-2"><FaArrowRight className="text-danger me-2" />90% of research hypotheses fail in experimental validation</li>
                          <li className="mb-2"><FaArrowRight className="text-danger me-2" />Drug discovery takes 10-15 years and costs $2.6 billion per approved drug</li>
                          <li className="mb-2"><FaArrowRight className="text-danger me-2" />Climate models require months of computation for single scenarios</li>
                          <li><FaArrowRight className="text-danger me-2" />Materials discovery relies on trial-and-error approaches</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-6 mb-4">
                  <div className="card h-100 border-0 shadow-lg bg-primary text-white">
                    <div className="card-body p-5">
                      <h3 className="card-title display-6 fw-bold mb-4">
                        <FaLightbulb className="me-3" />
                        AI Solution Impact
                      </h3>
                      <div className="row g-3">
                        {[
                          { text: "10,000x faster computational analysis", icon: <FaBolt /> },
                          { text: "95% reduction in failed experiments", icon: <FaCheckCircle /> },
                          { text: "80% cost reduction in R&D", icon: <FaChartLine /> },
                          { text: "Continuous 24/7 research progress", icon: <FaClock /> }
                        ].map((item, index) => (
                          <div key={index} className="col-12">
                            <div className="d-flex align-items-center p-3 bg-white bg-opacity-10 rounded">
                              <div className="text-success me-3">
                                {item.icon}
                              </div>
                              <span className="fw-semibold">{item.text}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Features */}
              <div className="card border-0 shadow-lg mb-5">
                <div className="card-body p-5">
                  <h2 className="text-center display-5 fw-bold text-primary mb-5">
                    How AI Transforms Scientific Research
                  </h2>
                  <div className="row g-4">
                    {aiFeatures.map((feature, index) => (
                      <div key={index} className="col-lg-4">
                        <div className="text-center h-100">
                          <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                               style={{ 
                                 width: '80px', 
                                 height: '80px',
                                 background: `linear-gradient(45deg, ${index === 0 ? '#007bff, #20c997' : index === 1 ? '#6f42c1, #e83e8c' : '#28a745, #17a2b8'})`
                               }}>
                            <div className="text-white">
                              {feature.icon}
                            </div>
                          </div>
                          <h3 className="h4 fw-bold mb-3">{feature.title}</h3>
                          <p className="text-muted">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Research Fields Tab */}
          {activeTab === 'fields' && (
            <div className="tab-pane fade show active">
              <div className="text-center mb-5">
                <h2 className="display-4 fw-bold text-primary mb-4">Scientific Research Domains</h2>
                <p className="lead fs-4 text-muted">
                  Comprehensive AI applications across all major scientific disciplines, 
                  revolutionizing research methodologies and accelerating discovery timelines.
                </p>
              </div>
              
              <div className="row g-4">
                {researchFields.map((field, index) => (
                  <div key={index} className="col-lg-6 mb-4">
                    <div className="card h-100 border-0 shadow-lg hover-lift">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start mb-4">
                          <div className={`bg-${field.color} text-white p-3 rounded me-4 flex-shrink-0`}>
                            {field.icon}
                          </div>
                          <div className="flex-grow-1">
                            <h3 className="card-title h4 fw-bold mb-3">{field.title}</h3>
                            <p className="card-text text-muted mb-4">{field.description}</p>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <h5 className="fw-bold text-primary mb-3">
                              <FaBook className="me-2" />
                              Key Applications
                            </h5>
                            <ul className="list-unstyled">
                              {field.applications.map((app, i) => (
                                <li key={i} className="mb-2">
                                  <FaArrowRight className="text-success me-2" />
                                  <small>{app}</small>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="col-md-6">
                            <h5 className="fw-bold text-primary mb-3">
                              <FaTrophy className="me-2" />
                              Expected Impact
                            </h5>
                            <div className={`alert alert-${field.color} alert-dismissible border-0`}>
                              <small>{field.impact}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Future Trends Tab */}
          {activeTab === 'future' && (
            <div className="tab-pane fade show active">
              <div className="text-center mb-5">
                <h2 className="display-4 fw-bold text-primary mb-4">Future of AI in Scientific Research</h2>
                <p className="lead fs-4 text-muted">
                  Emerging trends and breakthrough applications that will define the next decade of scientific discovery, 
                  with quantum computing integration and autonomous research systems leading the revolution.
                </p>
              </div>

              {/* Timeline */}
              <div className="row g-4 mb-5">
                {futureTrends.map((trend, index) => (
                  <div key={index} className="col-lg-6 mb-4">
                    <div className="card border-0 shadow-lg h-100">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center mb-3">
                          <span className={`badge bg-${trend.color} fs-6 px-3 py-2 me-3`}>
                            {trend.year}
                          </span>
                          <FaRocket className={`text-${trend.color}`} />
                        </div>
                        <h3 className="card-title h4 fw-bold mb-3">{trend.title}</h3>
                        <p className="card-text text-muted mb-4">{trend.description}</p>
                        <div>
                          <h6 className="fw-bold text-primary mb-2">Key Research Areas:</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {trend.fields.map((field, i) => (
                              <span key={i} className={`badge bg-${trend.color} bg-opacity-25 text-${trend.color} px-2 py-1`}>
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quantum Computing Section */}
              <div className="card border-0 shadow-lg bg-dark text-white">
                <div className="card-body p-5">
                  <div className="text-center mb-5">
                    <FaMicrochip size={60} className="text-info mb-4" />
                    <h3 className="display-6 fw-bold mb-4">Quantum-AI Integration: The Next Frontier</h3>
                    <p className="lead">
                      The convergence of quantum computing and artificial intelligence will unlock computational capabilities 
                      that are fundamentally impossible with classical systems, revolutionizing scientific simulation and modeling.
                    </p>
                  </div>
                  
                  <div className="row g-4">
                    <div className="col-lg-6">
                      <div className="card bg-white bg-opacity-10 border-0">
                        <div className="card-body p-4">
                          <h4 className="card-title text-info mb-4">Quantum Advantages in Research</h4>
                          <ul className="list-unstyled">
                            {[
                              "Exponential speedup in molecular simulation",
                              "Quantum machine learning algorithms",
                              "Optimization of complex multi-variable problems",
                              "Cryptographic protocol development"
                            ].map((item, i) => (
                              <li key={i} className="mb-3">
                                <FaCheckCircle className="text-success me-2" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-lg-6">
                      <div className="card bg-white bg-opacity-10 border-0">
                        <div className="card-body p-4">
                          <h4 className="card-title text-info mb-4">Target Applications by 2027</h4>
                          <ul className="list-unstyled">
                            {[
                              { text: "Room-temperature superconductor discovery", icon: <FaAtom /> },
                              { text: "Complete protein folding prediction", icon: <FaDna /> },
                              { text: "Perfect climate system modeling", icon: <FaGlobe /> },
                              { text: "Novel pharmaceutical compound design", icon: <FaFlask /> }
                            ].map((item, i) => (
                              <li key={i} className="mb-3">
                                <span className="text-warning me-2">{item.icon}</span>
                                {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Capabilities Tab */}
          {activeTab === 'capabilities' && (
            <div className="tab-pane fade show active">
              <div className="text-center mb-5">
                <h2 className="display-4 fw-bold text-primary mb-4">AI Research Capabilities</h2>
                <p className="lead fs-4 text-muted">
                  Advanced artificial intelligence systems providing unprecedented research acceleration, 
                  precision, and breakthrough discovery potential across all scientific domains.
                </p>
              </div>

              {/* Core Capabilities */}
              <div className="row g-4 mb-5">
                {capabilities.map((capability, index) => (
                  <div key={index} className="col-lg-3 col-md-6">
                    <div className="card border-0 shadow-lg text-center h-100">
                      <div className="card-body p-4">
                        <div className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-4 bg-${capability.color} text-white`} 
                             style={{ width: '80px', height: '80px' }}>
                          {capability.icon}
                        </div>
                        <h3 className="card-title h5 fw-bold mb-3">{capability.title}</h3>
                        <p className="card-text text-muted">{capability.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Workflow */}
              <div className="card border-0 shadow-lg mb-5">
                <div className="card-body p-5">
                  <h3 className="text-center display-6 fw-bold text-primary mb-5">AI-Driven Research Workflow</h3>
                  
                  <div className="row g-4">
                    {workflowSteps.map((step, index) => (
                      <div key={index} className="col-lg-2 col-md-4 col-6">
                        <div className="text-center">
                          <div className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-3 bg-${step.color} text-white fw-bold`} 
                               style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                            {step.step}
                          </div>
                          <h4 className="h6 fw-bold text-dark mb-2">{step.title}</h4>
                          <p className="small text-muted">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Advanced Capabilities */}
              <div className="row g-4">
                <div className="col-lg-6">
                  <div className="card border-0 shadow-lg h-100">
                    <div className="card-body p-4">
                      <h4 className="card-title fw-bold text-primary mb-4">
                        <FaBrain className="me-2" />
                        Machine Learning & Deep Learning
                      </h4>
                      <div className="row g-3">
                        {[
                          { title: "Neural Network Architectures", desc: "Transformer models, CNNs, RNNs, and GNNs optimized for scientific data analysis", color: "primary" },
                          { title: "Reinforcement Learning", desc: "Autonomous experiment design and optimization through trial-and-error learning", color: "success" },
                          { title: "Generative AI Models", desc: "Creating novel molecular structures, materials, and experimental protocols", color: "info" }
                        ].map((item, i) => (
                          <div key={i} className="col-12">
                            <div className={`border-start border-5 border-${item.color} ps-3`}>
                              <h6 className="fw-bold mb-1">{item.title}</h6>
                              <p className="small text-muted mb-0">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card border-0 shadow-lg h-100">
                    <div className="card-body p-4">
                      <h4 className="card-title fw-bold text-primary mb-4">
                        <FaMicrochip className="me-2" />
                        High-Performance Computing
                      </h4>
                      <div className="row g-3">
                        {[
                          { title: "Distributed Computing", desc: "Massively parallel processing across cloud and edge computing infrastructure", color: "danger" },
                          { title: "GPU Acceleration", desc: "CUDA and OpenCL optimization for rapid mathematical computations", color: "warning" },
                          { title: "Quantum-Classical Hybrid", desc: "Integration with quantum processors for exponential computational advantages", color: "secondary" }
                        ].map((item, i) => (
                          <div key={i} className="col-12">
                            <div className={`border-start border-5 border-${item.color} ps-3`}>
                              <h6 className="fw-bold mb-1">{item.title}</h6>
                              <p className="small text-muted mb-0">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary text-white py-5">
        <div className="container text-center">
          <h2 className="display-5 fw-bold mb-4">Ready to Revolutionize Your Research?</h2>
          <p className="lead mb-4 fs-4">
            Join leading research institutions and technology companies leveraging AI to accelerate 
            scientific discovery and solve humanity's greatest challenges.
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
            <button className="btn btn-light btn-lg px-5 py-3 fw-bold">
              <FaUsers className="me-2" />
              Partner With Us
            </button>
            <button className="btn btn-outline-light btn-lg px-5 py-3 fw-bold">
              <FaRocket className="me-2" />
              Explore Solutions
            </button>
          </div>
        </div>
      </div>
      </div>
  )
}
export default AIScientificResearch;
     